import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessageOf, isApiError, type FieldErrors } from '../../utils/apiErrors';
import { useNotifications } from '../NotificationProvider';

export const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const useFormDeleteEvent = (deleteHandler: () => void) => {
  const handlerRef = useRef(deleteHandler);
  handlerRef.current = deleteHandler;
  useEffect(() => {
    const handler = () => handlerRef.current();
    window.addEventListener('form-delete', handler);
    return () => window.removeEventListener('form-delete', handler);
  }, []);
};

/**
 * First missing required field, as a ready-to-show message.
 *
 * Most form pages guarded saving with a bare `if (!canSave) return;`, so
 * pressing Save on an incomplete form did nothing at all and said nothing —
 * indistinguishable from a broken button. Fields are checked in the order
 * given, so the message points at the first gap rather than listing every one.
 *
 * Empty means: null/undefined, a blank or whitespace-only string, an empty
 * array, or NaN. Zero and `false` are legitimate values and count as present.
 */
export function firstMissingField(
  fields: { label: string; value: unknown }[]
): string | null {
  for (const { label, value } of fields) {
    const missing =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'number' && Number.isNaN(value));
    if (missing) return `${label} is required.`;
  }
  return null;
}

/**
 * Field-level server errors for a form.
 *
 * `setFromError(err)` reads the field errors an ApiError carries (e.g. a 422
 * naming `email`) so each can be shown under its own field; `clear(field)`
 * removes one as soon as the user edits it. Returns true when at least one
 * field was named, so the caller can skip the generic toast.
 */
export function useFieldErrors() {
  const [errors, setErrors] = useState<FieldErrors>({});
  const setFromError = useCallback((err: unknown): boolean => {
    const next = isApiError(err) ? err.fieldErrors : {};
    setErrors(next);
    return Object.keys(next).length > 0;
  }, []);
  const clear = useCallback((field?: string) => {
    setErrors(prev => {
      if (!field) return Object.keys(prev).length ? {} : prev;
      if (!(field in prev)) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);
  return { errors, errorFor: (field: string) => errors[field], setFromError, clear };
}

/**
 * Reports a failed save: field-level errors from a 422 go under their fields
 * (with a short toast pointing at them); anything else gets the plain-language
 * message, or `fallback` when the error carries none.
 */
export function reportSaveError(
  err: unknown,
  fieldErrors: Pick<ReturnType<typeof useFieldErrors>, 'setFromError'>,
  showError: (message: string) => void,
  fallback: string
): void {
  if (fieldErrors.setFromError(err)) {
    showError(errorMessageOf(err, 'Please check the highlighted fields.'));
  } else {
    showError(errorMessageOf(err, fallback));
  }
}

/**
 * Drops a field's server error as soon as the user edits that field, so the
 * red outline never outlives the value it was about. `form` is the form's
 * current values (one flat object); `apiField` maps a form key to the API's
 * name for it where they differ (e.g. `dueDateISO` -> `due_date`).
 */
export function useClearErrorsOnEdit(
  form: Record<string, unknown>,
  fieldErrors: Pick<ReturnType<typeof useFieldErrors>, 'clear'>,
  apiField: Record<string, string> = {}
): void {
  const previous = useRef(form);
  const apiFieldRef = useRef(apiField);
  apiFieldRef.current = apiField;
  useEffect(() => {
    const before = previous.current;
    previous.current = form;
    for (const key of Object.keys(form)) {
      if (!Object.is(before[key], form[key])) fieldErrors.clear(apiFieldRef.current[key] ?? key);
    }
  }, [form, fieldErrors.clear]);
}

export type FieldErrorsApi = ReturnType<typeof useFieldErrors>;

interface FormSaveOptions {
  /** Toast text for a failure that carries no plain-language message of its own. */
  fallback?: string;
  /** Share a `useFieldErrors()` the form already holds; otherwise one is created. */
  fieldErrors?: FieldErrorsApi;
  /** Window event that triggers the save. Defaults to the header's `form-save`. */
  event?: string;
}

/**
 * Runs `saveHandler` when the header's Save button fires `form-save`, and is
 * the default error handling for every FormPage-based form.
 *
 * Anything the handler throws or rejects with is reported the same way: a 422's
 * field errors are kept for the form to show under their fields, and a
 * plain-language toast is shown (see `reportSaveError`). Previous field errors
 * are cleared at the start of each save. The returned `fieldErrors` is what a
 * form passes to its inputs as `error={fieldErrors.errorFor('<api field>')}`;
 * `formErrorsGuard.test.ts` fails the build for a form that does not.
 *
 * A save that is still running ignores further `form-save` events, so a
 * double click or a second Enter cannot submit the form twice.
 *
 * A handler should therefore let a failed save throw (and not call `onSave`)
 * rather than catch it and toast a fixed string.
 */
export function useFormSaveEvent(saveHandler: () => unknown, options: FormSaveOptions = {}): FieldErrorsApi {
  const { showError } = useNotifications();
  const ownErrors = useFieldErrors();
  const fieldErrors = options.fieldErrors ?? ownErrors;
  const eventName = options.event ?? 'form-save';

  const handlerRef = useRef(saveHandler);
  handlerRef.current = saveHandler;
  const reportRef = useRef<(err: unknown) => void>(() => {});
  reportRef.current = (err: unknown) =>
    reportSaveError(err, fieldErrors, showError, options.fallback ?? 'Failed to save. Please try again.');
  const clearRef = useRef(fieldErrors.clear);
  clearRef.current = fieldErrors.clear;

  // True from the moment an async save starts until it settles. A second
  // click or Enter in that window would send the same create twice, so it is
  // ignored (a failed save settles too, so the user can fix and retry).
  const savingRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      if (savingRef.current) return;
      clearRef.current();
      try {
        const result = handlerRef.current();
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          savingRef.current = true;
          (result as Promise<unknown>)
            .then(undefined, err => reportRef.current(err))
            .then(() => { savingRef.current = false; });
        }
      } catch (err) {
        reportRef.current(err);
      }
    };
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName]);

  return fieldErrors;
}
